import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  EntityDtoContainerOfOrganizationalUnitDto,
  OrganizationalUnitDto,
  OrganizationalUnitService,
} from '@swagger/humanresources';
import { BranchOption } from '@features/users/util-reports';

const BRANCH_TYPE = 'BRANCH';

/**
 * The branches the user may see, flattened from the OU tree in one request.
 * Deliberately not the `sessionStorage['ous']` cache of the administration
 * domain: a domain does not read another domain's cache (#5248).
 */
@Injectable({ providedIn: 'root' })
export class BranchClient {
  #organizationalUnits = inject(OrganizationalUnitService);

  async listBranches(): Promise<BranchOption[]> {
    const response = await firstValueFrom(
      this.#organizationalUnits.organizationalUnitQueryOu({ body: {} }),
    );
    return collectBranches(response.result ?? [])
      .map(toBranchOption)
      .sort((a, b) => a.key.localeCompare(b.key));
  }
}

function collectBranches(units: OrganizationalUnitDto[]): OrganizationalUnitDto[] {
  const branches: OrganizationalUnitDto[] = [];
  for (const unit of units) {
    if (unit.type === BRANCH_TYPE && unit.uId) {
      branches.push(unit);
    }
    branches.push(...collectBranches(childUnits(unit)));
  }
  return branches;
}

function childUnits(unit: OrganizationalUnitDto): OrganizationalUnitDto[] {
  return (unit.children ?? [])
    .map((child: EntityDtoContainerOfOrganizationalUnitDto) => child.data)
    .filter((child): child is OrganizationalUnitDto => !!child);
}

function toBranchOption(unit: OrganizationalUnitDto): BranchOption {
  return { uId: unit.uId ?? '', key: unit.key ?? unit.shortName ?? '', name: unit.name ?? '' };
}
